import React, { useState, useMemo } from 'react';
import { X } from 'lucide-react';
import ArtworkCard from '../components/ArtworkCard';
import GalleryFilters from '../components/GalleryFilters';

const GalleryPage = ({ artworks = [], categories = [], siteSettings = {} }) => {
    const [selectedCategory, setSelectedCategory] = useState("all");
    const [selectedTags, setSelectedTags] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [viewMode, setViewMode] = useState("grid");
    const [selectedArtwork, setSelectedArtwork] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);

    const isLoading = false;
    const ITEMS_PER_PAGE = 12;

    // Derive tags from artworks
    const allTags = useMemo(() => {
        const tagsSet = new Set();
        (artworks || []).forEach((artwork) => {
            (artwork.tags || []).forEach((tag) => tagsSet.add(tag));
        });
        return Array.from(tagsSet).sort();
    }, [artworks]);

    // Filter artworks
    const filteredArtworks = useMemo(() => {
        return (artworks || []).filter((artwork) => {
            if (selectedCategory !== "all" && artwork.category !== selectedCategory) {
                return false;
            }
            if (selectedTags.length > 0) {
                const hasMatchingTag = selectedTags.some((tag) => (artwork.tags || []).includes(tag));
                if (!hasMatchingTag) return false;
            }
            if (searchQuery) {
                const query = searchQuery.toLowerCase();
                const searchableText = [
                  artwork.title, artwork.artist, artwork.description,
                  artwork.medium, artwork.category, ...(artwork.tags || []),
                ].filter(Boolean).join(" ").toLowerCase();
                if (!searchableText.includes(query)) return false;
            }
            return true;
        });
    }, [artworks, selectedCategory, selectedTags, searchQuery]);

    // Reset to page 1 when filters change
    React.useEffect(() => {
        setCurrentPage(1);
    }, [selectedCategory, selectedTags, searchQuery]);

    // Calculate pagination values
    const totalPages = Math.ceil(filteredArtworks.length / ITEMS_PER_PAGE);
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = Math.min(startIndex + ITEMS_PER_PAGE, filteredArtworks.length);
    const paginatedArtworks = filteredArtworks.slice(startIndex, endIndex);
    
    const handleCardClick = (artwork) => {
        setSelectedArtwork(artwork);
    };

    const handleInquire = () => {
        if (!selectedArtwork) return;
        
        const imageUrl = selectedArtwork.imageUrl;
        const message = `Hi! I'm interested in the artwork "${selectedArtwork.title}". \nImage: ${imageUrl}\nCan you provide more details?`;
        
        const whatsappLink = siteSettings.whatsappLink || 'https://wa.me/94773643253';
        const encodedMessage = encodeURIComponent(message);
        
        window.open(`${whatsappLink}?text=${encodedMessage}`, '_blank');
    };

    // Modal Overlay Component (Fully Responsive)
    const ArtworkModal = ({ artwork, onClose }) => {
        if (!artwork) return null;

        return (
            <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-2 sm:p-4 overflow-y-auto">
                {/* Modal Container - Full content visible */}
                <div className="bg-white rounded-xl sm:rounded-2xl w-full max-w-2xl my-auto flex flex-col max-h-[95vh]">
                    {/* Header */}
                    <div className="bg-white border-b border-gray-200 p-3 sm:p-6 flex items-center justify-between gap-2 flex-shrink-0">
                        <h2 className="text-lg sm:text-2xl font-serif font-bold text-gray-800 truncate">{artwork.title}</h2>
                        <button
                            onClick={onClose}
                            className="p-1 sm:p-2 hover:bg-gray-100 rounded-lg transition flex-shrink-0"
                        >
                            <X size={20} className="sm:w-6 sm:h-6" />
                        </button>
                    </div>

                    {/* Image Section - Displays fully */}
                    <div className="bg-white px-3 sm:px-6 py-3 sm:py-4 flex-shrink-0 flex items-center justify-center">
                        <div className="bg-gray-200 rounded-lg overflow-hidden w-full flex items-center justify-center">
                            <img
                                src={artwork.imageUrl}
                                alt={artwork.title}
                                className="w-full h-auto object-contain max-h-[60vh]"
                                onError={(e) => {
                                    e.target.src = 'https://placehold.co/600x500/D4BBAA/333333?text=Image+Not+Found';
                                }}
                            />
                        </div>
                    </div>

                    {/* Content Section - Scrollable */}
                    <div className="p-3 sm:p-6 space-y-4 sm:space-y-6 overflow-y-auto flex-1 min-h-0">
                        {/* Category & Tags */}
                        <div className="flex flex-wrap gap-2">
                            {artwork.category && (
                                <span className="inline-block px-2 sm:px-3 py-1 bg-orange-100 text-orange-800 text-xs sm:text-sm font-semibold rounded-full capitalize">
                                    {artwork.category}
                                </span>
                            )}
                            {(artwork.tags || []).map((tag, index) => (
                                <span
                                    key={index}
                                    className="inline-block px-2 sm:px-3 py-1 bg-gray-100 text-gray-700 text-xs sm:text-sm rounded-full"
                                >
                                    {tag}
                                </span>
                            ))}
                        </div>

                        {/* Description */}
                        {artwork.description && (
                            <div>
                                <p className="text-xs sm:text-base text-gray-600 leading-relaxed">
                                    {artwork.description}
                                </p>
                            </div>
                        )}

                        {/* Details */}
                        <div className="grid grid-cols-2 gap-3 sm:gap-4 pt-4 border-t border-gray-200">
                            {artwork.medium && (
                                <div>
                                    <p className="text-xs sm:text-sm text-gray-600 font-semibold">Medium</p>
                                    <p className="text-xs sm:text-base text-gray-800">{artwork.medium}</p>
                                </div>
                            )}
                            {artwork.dimensions && (
                                <div>
                                    <p className="text-xs sm:text-sm text-gray-600 font-semibold">Dimensions</p>
                                    <p className="text-xs sm:text-base text-gray-800">{artwork.dimensions}</p>
                                </div>
                            )}
                            {artwork.branch && (
                                <div className="col-span-2">
                                    <p className="text-xs sm:text-sm text-gray-600 font-semibold mb-2">Available at</p>
                                    <div className="flex flex-wrap gap-2">
                                        {Array.isArray(artwork.branch) ? (
                                            artwork.branch.map((location, index) => (
                                                <span key={index} className="inline-block px-2 sm:px-3 py-1 bg-orange-100 text-orange-800 text-xs sm:text-sm font-medium rounded-full">
                                                    {location}
                                                </span>
                                            ))
                                        ) : (
                                            <span className="inline-block px-2 sm:px-3 py-1 bg-orange-100 text-orange-800 text-xs sm:text-sm font-medium rounded-full">
                                                {artwork.branch}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Inquire Button - Always visible when scrolling */}
                        <button
                            onClick={handleInquire}
                            className="w-full px-4 sm:px-6 py-2 sm:py-3 bg-green-500 hover:bg-green-600 text-white text-sm sm:text-base font-semibold rounded-lg transition mt-4"
                        >
                            Inquire About This Artwork
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // Gallery Page
    return (
        <div className="min-h-screen bg-gray-50 py-8 sm:py-12 md:py-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="text-center mb-8 sm:mb-12">
                    <h1 className="text-2xl sm:text-4xl md:text-5xl font-serif font-bold text-gray-800 mb-2 sm:mb-4">
                        Our Gallery
                    </h1>
                    <p className="text-xs sm:text-base md:text-lg text-gray-600 max-w-2xl mx-auto px-2">
                        Explore our curated collection of artworks from local artists
                    </p>
                </div>

                {/* Filters */}
                <div className="mb-8 sm:mb-12 bg-white rounded-lg sm:rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6">
                    <GalleryFilters
                        categories={categories}
                        selectedCategory={selectedCategory}
                        setSelectedCategory={setSelectedCategory}
                        selectedTags={selectedTags}
                        setSelectedTags={setSelectedTags}
                        searchQuery={searchQuery}
                        setSearchQuery={setSearchQuery}
                        viewMode={viewMode}
                        setViewMode={setViewMode}
                        allTags={allTags}
                    />
                </div>

                {/* Results Count */}
                <div className="mb-6 text-xs sm:text-base text-gray-600">
                    {isLoading ? (
                        <div className="h-4 w-40 bg-gray-200 rounded animate-pulse"></div>
                    ) : (
                        <p>
                            Showing <span className="font-semibold text-gray-800">{paginatedArtworks.length}</span>{" "}
                            of <span className="font-semibold text-gray-800">{filteredArtworks.length}</span>{" "}
                            {filteredArtworks.length === 1 ? "item" : "items"}
                            {totalPages > 1 && <span> • Page {currentPage} of {totalPages}</span>}
                        </p>
                    )}
                </div>

                {/* Gallery Grid/List */}
                {isLoading ? (
                    <div className={viewMode === "grid" ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8" : "space-y-4 sm:space-y-6"}>
                        {Array(6).fill(0).map((_, i) => (
                            <div key={i} className={`rounded-xl bg-gray-200 animate-pulse ${viewMode === "grid" ? "h-64 sm:h-80" : "h-40 sm:h-48"}`}></div>
                        ))}
                    </div>
                ) : filteredArtworks.length === 0 ? (
                    <div className="text-center py-16 sm:py-20">
                        <div className="text-4xl sm:text-6xl mb-3 sm:mb-4">🎨</div>
                        <h3 className="text-xl sm:text-2xl font-serif font-bold text-gray-800 mb-2">No artworks found</h3>
                        <p className="text-sm sm:text-base text-gray-600">Try adjusting your filters or search query</p>
                    </div>
                ) : viewMode === "grid" ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8">
                        {paginatedArtworks.map((artwork) => (
                            <ArtworkCard 
                                key={artwork.id} 
                                artwork={artwork} 
                                viewMode="grid" 
                                onNavigate={() => handleCardClick(artwork)} 
                            />
                        ))}
                    </div>
                ) : (
                    <div className="space-y-4 sm:space-y-6">
                        {paginatedArtworks.map((artwork) => (
                            <ArtworkCard 
                                key={artwork.id} 
                                artwork={artwork} 
                                viewMode="list" 
                                onNavigate={() => handleCardClick(artwork)} 
                            />
                        ))}
                    </div>
                )}

                {/* Pagination Controls */}
                {totalPages > 1 && (
                    <div className="mt-12 sm:mt-16 flex items-center justify-center gap-2 sm:gap-4">
                        <button
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                            className="px-3 sm:px-4 py-2 sm:py-3 bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-gray-800 font-semibold rounded-lg transition"
                        >
                            ← Previous
                        </button>
                        
                        <div className="flex items-center gap-1 sm:gap-2">
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                                <button
                                    key={page}
                                    onClick={() => setCurrentPage(page)}
                                    className={`px-2 sm:px-3 py-1 sm:py-2 rounded-lg font-semibold transition text-xs sm:text-base ${
                                        currentPage === page
                                            ? 'bg-orange-500 text-white'
                                            : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
                                    }`}
                                >
                                    {page}
                                </button>
                            ))}
                        </div>

                        <button
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                            disabled={currentPage === totalPages}
                            className="px-3 sm:px-4 py-2 sm:py-3 bg-gray-200 hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed text-gray-800 font-semibold rounded-lg transition"
                        >
                            Next →
                        </button>
                    </div>
                )}
            </div>

            {/* Artwork Modal */}
            {selectedArtwork && (
                <ArtworkModal 
                    artwork={selectedArtwork} 
                    onClose={() => setSelectedArtwork(null)} 
                />
            )}
        </div>
    );
};

export default GalleryPage;